<template>
  <div
    class="bg-white border-t-4 shadow-lg p-4 max-w-25 w-screen z-50 transition-border transition-normal mt-2"
    :class="{
      'border-primary-500': status === null,
      'border-grey-600 arrow-grey': status === 0,
      'border-negative-600 arrow-negative': status === 1,
      'border-neutral-500 arrow-neutral': status === 2,
      'border-positive-600 arrow-positive': status === 3
    }"
  >
    <h2 class="text-xs font-bold uppercase text-grey-600 mb-1 flex">
      <icon
        v-if="!entry[`${type}_can_provide_feedback`] && !canEdit"
        icon="lock"
        class="w-4 h-4 icons-grey"
      />
      {{ statusTitle }} Status
    </h2>
    <ol
      v-if="entry[`${type}_can_provide_feedback`] && canEdit"
      class="list-reset mb-4"
    >
      <li
        v-for="option in selectableOptions"
        :key="`${entry.id}_${type}_status_${option.value}`"
        class="mb-2"
      >
        <label
          :for="`${entry.id}_${type}_status_${option.value}`"
          class="block transition-all transition-fast text-xs uppercase px-4 py-2 border font-bold cursor-pointer"
          :class="classObj(option.value)"
        >
          <input
            :id="`${entry.id}_${type}_status_${option.value}`"
            v-model="status"
            type="radio"
            class="sr-only"
            :value="option.value"
          />
          {{ option.text }}
        </label>
      </li>
    </ol>
    <div
      v-else-if="entry[`${type}_status`] === 0"
      class="mb-2 text-xs uppercase px-4 py-2 border font-bold cursor-pointer"
      :class="classObj(entry[`${type}_status`])"
    >
      No Status Provided
    </div>
    <div
      v-else-if="entry[`${type}_status`] !== null"
      class="mb-2 text-xs uppercase px-4 py-2 border font-bold cursor-pointer"
      :class="classObj(entry[`${type}_status`])"
    >
      {{ options[entry[`${type}_status`]].text }}
    </div>
    <div
      v-else
      class="mb-2 text-xs uppercase px-4 py-2 border font-bold cursor-pointer"
      :class="classObj()"
    >
      <icon icon="important" class="icons-primary w-4 h-4 align-middle mr-1" />
      Status Pending
    </div>
    <div
      v-if="
        (entry[`${type}_can_provide_feedback`] && canEdit) ||
          entry[`${type}_feedback`]
      "
    >
      <label
        :for="`${entry.id}_${type}_feedback`"
        class="block text-xs font-bold uppercase text-grey-600 mb-1"
        :class="{
          'text-negative-800': invalidEntry
        }"
      >
        {{ commentLabel }}
      </label>
      <textarea
        :id="`${entry.id}_${type}_feedback`"
        ref="textarea"
        v-model.trim="feedback"
        class="border-b outline-none w-full pb-4 leading-normal text-sm italic"
        :class="{
          'focus:border-negative-500':
            entry[`${type}_status`] < 3 &&
            canEdit &&
            entry[`${type}_can_provide_feedback`],
          'focus:border-primary-500':
            !canEdit && !entry[`${type}_can_provide_feedback`]
        }"
        placeholder="Any comments about your status?"
        maxlength="500"
        :disabled="!(entry[`${type}_can_provide_feedback`] && canEdit)"
        :readonly="!(entry[`${type}_can_provide_feedback`] && canEdit)"
      />
      <p v-if="invalidEntry" class="text-xs italic text-negative-800">
        As you have indicated that there have been issues, please make sure that
        you provide a comment for your project monitor.
      </p>
    </div>
    <div class="pt-2 flex justify-between">
      <p
        v-if="nameLookup && nameLookup[entry[`${type}_monitoring_by`]]"
        class="text-xs italic flex-1"
      >
        Updated by:
        {{ nameLookup[entry[`${type}_monitoring_by`]] }}
      </p>
      <icon-link href="#" icon="check" class="ml-auto" @click.prevent="close">
        <template v-if="entry[`${type}_can_provide_feedback`] && canEdit">
          Save &amp; Close
        </template>
        <template v-else>
          Close
        </template>
      </icon-link>
    </div>
  </div>
</template>

<script>
import autosize from "autosize";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import delay from "lodash/delay";
export default {
  name: "ProgressReportMarkerPopper",
  props: {
    canEdit: {
      type: Boolean,
      default: false
    },
    entry: {
      type: Object,
      required: true
    },
    isOpen: {
      type: Boolean,
      default: false
    },
    item: {
      type: Object,
      required: true
    },
    type: {
      type: String,
      required: false,
      default: "partner",
      validator(value) {
        return ["msp", "monitor", "partner"].includes(value);
      }
    }
  },
  computed: {
    nameLookup() {
      if (!window._partners && !window._monitors) return false;
      return Object.assign({}, window._partners, window._monitors);
    },
    commentLabel() {
      return !this.entry[`${this.type}_can_provide_feedback`] || !this.canEdit
        ? "Comment"
        : this.entry[`${this.type}_status`] < 3 &&
          this.entry[`${this.type}_status`] !== null
        ? "Comment Required"
        : "Comment";
    },
    statusTitle() {
      return format(parseISO(this.entry.open_date), "LL/yy");
    },
    options() {
      const { red_label, amber_label, green_label } = this.item;
      return [
        {
          text: "No Status",
          value: 0
        },
        {
          text: red_label,
          value: 1
        },
        {
          text: amber_label,
          value: 2
        },
        {
          text: green_label,
          value: 3
        }
      ];
    },
    selectableOptions() {
      return this.options.slice(1, 4);
    },
    invalidEntry() {
      return (
        this.entry[`${this.type}_status`] !== null &&
        !this.entry[`${this.type}_feedback`] &&
        this.entry[`${this.type}_status`] < 3 &&
        this.canEdit &&
        this.entry.partner_can_provide_feedback
      );
    },
    status: {
      get() {
        return this.entry[`${this.type}_status`];
      },
      set(value) {
        return this.$emit("progress:save", {
          id: this.entry.id,
          type: this.type,
          [`${this.type}_status`]: value,
          [`${this.type}_feedback`]: this.feedback
        });
      }
    },
    feedback: {
      get() {
        return this.entry[`${this.type}_feedback`];
      },
      set(value) {
        return this.$emit("progress:save", {
          id: this.entry.id,
          type: this.type,
          [`${this.type}_status`]: this.status,
          [`${this.type}_feedback`]: value
        });
      }
    }
  },
  mounted() {
    autosize(this.$refs.textarea);
    this.$parent.$on("show", () =>
      delay(() => {
        autosize.update(this.$refs.textarea);
      }, 10)
    );
  },
  methods: {
    close() {
      return this.$parent.doClose();
    },
    classObj(value) {
      switch (value) {
        case 0:
          return {
            "text-grey-600 border-grey-600": value !== this.status,
            "bg-grey-600 text-white border-grey-600": value === this.status
          };
        case 1:
          return {
            "text-negative-600 border-negative-600": value !== this.status,
            "bg-negative-600 text-white border-negative-600":
              value === this.status
          };
        case 2:
          return {
            "text-neutral-600 border-neutral-500": value !== this.status,
            "bg-neutral-500 text-white border-neutral-500":
              value === this.status
          };
        case 3:
          return {
            "text-positive-600 border-positive-600": value !== this.status,
            "bg-positive-600 text-white border-positive-600":
              value === this.status
          };
        default:
          return "text-primary-600 border-primary-600";
      }
    }
  }
};
</script>
